import { API_BASE_URL } from '@synergy/config';
import { AuctionIcon, PrintIcon } from '@synergy/theme-next';
import { Box, IconButton, Tooltip } from '@mui/material';
import { CheckOutlined } from '@mui/icons-material';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { InquiryOfferState } from '@synergy/models';
import { PaloColumn, PaloFilterChips, PaloListView, PaloStatusPill, usePaloListSort } from '@synergy/components-palo';
import { ReactElement, useEffect, useState } from 'react';
import { UrlFilter } from '@synergy/components';
import { getInquiryOfferStateTranslationId } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';
import { useSearchParams } from 'react-router-dom';
import printJS from 'print-js';
import urljoin from 'url-join';

import * as InquiryActions from '../actions/inquiry-actions';
import { Inquiry } from '../../../common/models/inquiry';
import { PaloDeadlinePill, PaloInquiryDeadline } from './palo-inquiry-deadline';
import { PaloInquiriesOverviewFilter } from './palo-inquiries-overview-filter';
import { daysLeft, deadlineLevel, titleWithoutMember } from './inquiry-deadline';
import { useSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

/**
 * The state the supplier is in on this inquiry.
 *
 * The legacy table reached straight into accountPurchases[0].offers[0], which
 * throws the moment an inquiry arrives without either — so this asks first.
 */
const offerState = (inquiry: Inquiry): InquiryOfferState | undefined =>
    inquiry.accountPurchases?.[0]?.offers?.[0]?.inquiryOfferState;

/** The member the inquiry is for; the first one where several share it. */
const memberOf = (inquiry: Inquiry): string | undefined => inquiry.accountPurchases?.[0]?.customerAccount?.name;

/**
 * How many members the inquiry is for. One member ordering to two of its
 * addresses has two purchases, but is still one member.
 */
const memberCount = (inquiry: Inquiry): number =>
    new Set((inquiry.accountPurchases ?? []).map(purchase => purchase.customerAccount?.id ?? purchase.id)).size;

/**
 * The supplier portal reads larger than the back office: suppliers open it now
 * and then rather than working in it all day (R-257). Kept to this list, so the
 * shared list and the admin stay as they are.
 */
const READABLE_ROWS = {
    display: 'contents',
    '& .MuiTableBody-root .MuiTableCell-root': { fontSize: '14px', height: 44 },
};

export const PaloInquiriesOverview = (): ReactElement => {
    const dispatch = useThunkDispatch();
    const intl = useIntl();
    const [searchParams, setSearchParams] = useSearchParams();
    // The list opens in its own order, and asks the server for it: the header
    // showed "Nummer" with an arrow while the request carried no order at all,
    // so the oldest document stood on top under a header saying the newest
    // (LST-S16, #2234).
    const { sort, params: sortedParams } = usePaloListSort('inquiries');
    const { data, requesting } = useSelector(state => state.inquiryData);
    const [, setLoadingPdf] = useState(false);

    const page = Number(searchParams.get('page') ?? 1); // 1-based
    const limit = Number(searchParams.get('limit') ?? 50);
    const items = data.items ?? [];
    const isOpenView = InquiryOfferState.Open === searchParams.get('filter[inquiryOfferStates]');
    // Most suppliers never see an auction, so the column only comes with one.
    const anyAuction = items.some(inquiry => inquiry.isAuction);
    const isOpen = (inquiry: Inquiry): boolean => InquiryOfferState.Open === offerState(inquiry);
    // Counted on the page shown; an open list rarely runs past one page. One
    // already past its deadline is marked in its row but does not end today.
    const endingToday = items.filter(inquiry => isOpen(inquiry) && 0 === daysLeft(inquiry.endDate)).length;
    const endingSoon = items.filter(inquiry => isOpen(inquiry) && 'soon' === deadlineLevel(inquiry.endDate)).length;

    useEffect(() => {
        dispatch(InquiryActions.fetchInquiries(sortedParams));

        return () => {
            dispatch(InquiryActions.resetInquiries());
        };
    }, [dispatch, sortedParams]);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const print = (id: number): void => {
        const url = urljoin(API_BASE_URL, `inquiries/${id}/pdf-offers`);

        printJS({
            printable: url,
            onLoadingStart: () => setLoadingPdf(true),
            onLoadingEnd: () => setLoadingPdf(false),
            onError: () => {
                // Not every browser can print for us; then just show the file.
                setLoadingPdf(false);
                window.open(url);
            },
        });
    };

    const columns: PaloColumn<Inquiry>[] = [
        {
            key: 'number',
            type: 'documentNumber',
            head: <FormattedMessage id="app.table.column.number" />,
            sortKey: 'number',
            cell: inquiry => inquiry.number,
        },
        {
            key: 'endDate',
            type: 'date',
            head: <FormattedMessage id="app.table.column.endDate" />,
            sortKey: 'endDate',
            cell: inquiry => <PaloInquiryDeadline endDate={inquiry.endDate} open={isOpen(inquiry)} />,
        },
        {
            key: 'member',
            type: 'name',
            head: <FormattedMessage id="app.table.column.member" />,
            cell: inquiry => {
                const more = memberCount(inquiry) - 1;

                return (
                    <>
                        {memberOf(inquiry) ?? '–'}
                        {more > 0 && (
                            <Box component="span" sx={{ color: tokens.color.ink2, ml: '6px' }}>
                                <FormattedMessage id="app.inquiries.moreMembers" values={{ count: more }} />
                            </Box>
                        )}
                    </>
                );
            },
        },
        {
            key: 'title',
            type: 'name',
            head: <FormattedMessage id="app.table.column.inquiry" />,
            sortKey: 'title',
            cell: inquiry => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                    {inquiry.hasTransitions && (
                        <CheckOutlined sx={{ fontSize: 15, color: tokens.color.ok, flexShrink: 0 }} />
                    )}
                    <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {titleWithoutMember(inquiry.title, memberOf(inquiry))}
                    </Box>
                </Box>
            ),
        },
        {
            key: 'status',
            type: 'status',
            head: <FormattedMessage id="app.table.column.status" />,
            cell: inquiry => {
                const state = offerState(inquiry);

                return state ? (
                    <PaloStatusPill
                        status={state}
                        label={<FormattedMessage id={getInquiryOfferStateTranslationId(state)} />}
                    />
                ) : null;
            },
        },
        ...(anyAuction
            ? [
                  {
                      key: 'auction',
                      type: 'flag',
                      head: <FormattedMessage id="app.table.column.auction" />,
                      cell: inquiry => inquiry.isAuction && <AuctionIcon sx={{ fontSize: 15 }} />,
                  } as PaloColumn<Inquiry>,
              ]
            : []),
        {
            key: 'createdAt',
            type: 'date',
            head: <FormattedMessage id="app.table.column.publishDate" />,
            sortKey: 'createdAt',
            cell: inquiry => (
                <Box component="span" sx={{ color: tokens.color.ink2 }}>
                    <FormattedDate value={inquiry.createdAt} year="numeric" month="2-digit" day="2-digit" />
                </Box>
            ),
        },
        {
            key: 'print',
            type: 'icon',
            head: '',
            cell: inquiry => (
                <Tooltip title={<FormattedMessage id="app.inquiries.print" />} placement="top">
                    <IconButton
                        size="small"
                        onClick={event => {
                            // The row opens the inquiry; this only prints.
                            event.stopPropagation();
                            print(inquiry.id);
                        }}
                    >
                        <PrintIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    return (
        <UrlFilter useLegacyFilter={true}>
            <Box sx={READABLE_ROWS}>
                <PaloListView<Inquiry>
                    title={<FormattedMessage id="app.inquiries.overview" />}
                    filters={<PaloInquiriesOverviewFilter />}
                    filterChips={<PaloFilterChips />}
                    notice={
                        isOpenView && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: '10px',
                                    mb: '12px',
                                }}
                            >
                                <Box sx={{ fontSize: '18px', fontWeight: 680, color: tokens.color.ink }}>
                                    <FormattedMessage
                                        id="app.inquiries.openCount"
                                        values={{ count: data.total ?? 0 }}
                                    />
                                </Box>
                                {endingToday > 0 && (
                                    <PaloDeadlinePill level="today">
                                        <FormattedMessage
                                            id="app.inquiries.endsToday"
                                            values={{ count: endingToday }}
                                        />
                                    </PaloDeadlinePill>
                                )}
                                {endingSoon > 0 && (
                                    <PaloDeadlinePill level="soon">
                                        <FormattedMessage id="app.inquiries.endsSoon" values={{ count: endingSoon }} />
                                    </PaloDeadlinePill>
                                )}
                                {'endDate' === sort.key && (
                                    <Box sx={{ ml: 'auto', fontSize: '13px', color: tokens.color.ink2 }}>
                                        <FormattedMessage id="app.inquiries.sortedByEndDate" />
                                    </Box>
                                )}
                            </Box>
                        )
                    }
                    sort={sort}
                    onSortChange={(key, order) => updateParams({ sort: key, order, page: '1' })}
                    searchValue={searchParams.get('search') ?? ''}
                    searchPlaceholder={intl.formatMessage({ id: 'app.common.searchPlaceholder' })}
                    onSearchChange={value => {
                        const next = new URLSearchParams(searchParams);
                        if (value) {
                            next.set('search', value);
                        } else {
                            next.delete('search');
                        }
                        next.set('page', '1');
                        setSearchParams(next, { replace: true });
                    }}
                    columns={columns}
                    items={items}
                    getRowId={inquiry => inquiry.id}
                    requesting={requesting}
                    emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
                    rowHref={inquiry => '/inquiries/' + inquiry.id}
                    pagination={{
                        count: data.total,
                        page,
                        limit,
                        onPageChange: newPage => updateParams({ page: String(newPage) }),
                        onLimitChange: newLimit => updateParams({ limit: String(newLimit), page: '1' }),
                    }}
                />
            </Box>
        </UrlFilter>
    );
};
